import {
  type LocalSubmissionStatusItem,
  type RejectKind,
  type SourceImport,
  type SourceImportDetail,
  type SourceImportProgress,
  type SourceImportState,
  type SourceImportStatusItem,
  type UploadChannel,
  type UploadPhase,
  type UploadProgress,
  type UploadTask,
  sourceImportProgressSchema,
} from '../types/uploads';

export function rejectionPhase(kind: RejectKind | null): UploadPhase {
  if (kind === 'quality') return 'rejected-quality';
  if (kind === 'exact_duplicate' || kind === 'near_duplicate' || kind === 'prefix_duplicate') {
    return 'rejected-duplicate';
  }
  return 'failed';
}

/**
 * Phase for a *terminal rejection* whose kind may be unknown. The batch status contract
 * (`POST /source-imports/status`) is not required to carry a `rejectKind`, so when it is absent this
 * groups the row as a content rejection (`rejected-quality`) rather than the `failed` (retryable) phase
 * that `rejectionPhase(null)` yields — the authoritative `failure` text is surfaced as the row reason.
 */
function batchRejectionPhase(kind: RejectKind | null | undefined): UploadPhase {
  return kind == null ? 'rejected-quality' : rejectionPhase(kind);
}

function progressForStage(
  state: SourceImportState,
  progress: SourceImportProgress | null | undefined,
): UploadProgress | undefined {
  if (state === 'probing' && progress && 'fetchedBytes' in progress) {
    return { completed: progress.fetchedBytes, total: progress.totalBytes, unit: 'bytes' };
  }
  if (state === 'pdq_check' && progress && 'sampledFrames' in progress) {
    return { completed: progress.sampledFrames, total: progress.plannedFrames, unit: 'frames' };
  }
  if (state === 'transferring' && progress && 'copiedBytes' in progress) {
    return { completed: progress.copiedBytes, total: progress.totalBytes, unit: 'bytes' };
  }
  return undefined;
}

/** Project the backend's closed source-import state into the unified transfer row. */
export function sourceImportPatch(source: SourceImport | SourceImportDetail): Partial<UploadTask> {
  const progress = 'progress' in source ? source.progress : undefined;
  const common: Partial<UploadTask> = {
    displayName: source.filename,
    mediaType: source.mediaType,
    contentBlake3: source.contentBlake3 ?? undefined,
    totalBytes: source.expectedBytes ?? 0,
    attemptLabel:
      source.attempts > 0 ? `retry ${source.attempts}/${source.maxAttempts}` : undefined,
    rejectKind: source.rejectKind ?? undefined,
    progress: progressForStage(source.state, progress),
  };
  switch (source.state) {
    case 'pending':
      return { ...common, phase: 'queued', importStage: undefined };
    case 'probing':
      if (source.mediaType === 'mcap') {
        return { ...common, phase: 'creating', importStage: undefined, progress: undefined };
      }
      return { ...common, phase: 'prechecking', importStage: 'remote-quality' };
    case 'pdq_check':
      if (source.mediaType === 'mcap') {
        return { ...common, phase: 'creating', importStage: undefined, progress: undefined };
      }
      return { ...common, phase: 'prechecking', importStage: 'remote-duplicate' };
    case 'transferring':
      return { ...common, phase: 'transferring', importStage: 'server-copy' };
    case 'transferred':
      return {
        ...common,
        phase: 'processing',
        importStage: source.mediaType === 'mcap' ? 'mcap-processing' : 'intake-processing',
        progress: undefined,
        intakeStarted: source.mediaType === 'video',
      };
    case 'rejected':
      return {
        ...common,
        phase: rejectionPhase(source.rejectKind),
        reason: source.rejectReason ?? 'Import rejected',
        importStage: undefined,
        progress: undefined,
      };
    case 'failed':
      return {
        ...common,
        phase: 'failed',
        reason: source.rejectReason ?? 'Import failed',
        importStage: undefined,
        progress: undefined,
      };
  }
}

// ── Batch status projection (POST /source-imports/status → transfer row) ───────────────────────────

export interface StatusContext {
  /** The transfer channel, so `importStage`/`intakeStarted` are only set for server-side imports. */
  channel: UploadChannel;
  /** Known media type (from the create/upload path); falls back to whichever ingest field is present. */
  mediaType?: 'video' | 'mcap';
}

function batchProgress(state: SourceImportState, raw: unknown): UploadProgress | undefined {
  const parsed = sourceImportProgressSchema.safeParse(raw);
  return parsed.success ? progressForStage(state, parsed.data) : undefined;
}

/** Post-transfer video intake projection (`intakeState`), consistent with the old `pollIntake` mapping. */
function intakeStatusPatch(
  status: IngestStatus,
  failure: string | undefined,
  isImport: boolean,
): Partial<UploadTask> {
  switch (status.intakeState) {
    case 'accepted':
      return {
        phase: 'accepted',
        assetId: status.assetId ?? undefined,
        reason: undefined,
        importStage: undefined,
        progress: undefined,
      };
    case 'rejected':
      return {
        phase: batchRejectionPhase(status.rejectKind),
        rejectKind: status.rejectKind ?? undefined,
        reason: failure ?? 'Rejected',
        importStage: undefined,
        progress: undefined,
        intakeStarted: isImport ? true : undefined,
      };
    case 'failed':
      return {
        phase: 'failed',
        reason: failure ?? 'Processing failed',
        importStage: undefined,
        progress: undefined,
        intakeStarted: isImport ? true : undefined,
      };
    default:
      // queued / probing / dedup_check / pii_gate / null — still processing.
      return {
        phase: 'processing',
        importStage: isImport ? 'intake-processing' : undefined,
        intakeStarted: isImport ? true : undefined,
        progress: undefined,
      };
  }
}

/** Post-transfer MCAP scanner projection (`mcapStatus`), consistent with the old `pollMcapIngest`. */
function mcapStatusPatch(
  status: IngestStatus,
  failure: string | undefined,
  isImport: boolean,
): Partial<UploadTask> {
  const recheck = {
    mcapCheckGeneration: status.mcapCheckGeneration ?? undefined,
    mcapTaskId: status.mcapTaskId ?? undefined,
    mcapRecheckable: status.mcapRecheckable === true,
  };
  switch (status.mcapStatus) {
    case 'ready':
      return {
        phase: 'accepted',
        assetId: status.assetId ?? undefined,
        reason: undefined,
        importStage: undefined,
        progress: undefined,
        mcapFailureCode: undefined,
        mcapCheckGeneration: undefined,
        mcapTaskId: undefined,
        mcapRecheckable: false,
      };
    case 'failed':
      return {
        ...recheck,
        phase: 'failed',
        reason: failure ?? 'MCAP processing failed',
        mcapFailureCode: status.failureCode ?? undefined,
        importStage: undefined,
        progress: undefined,
      };
    default:
      // queued / null — still processing.
      return {
        ...recheck,
        phase: 'processing',
        importStage: isImport ? 'mcap-processing' : undefined,
        progress: undefined,
      };
  }
}

/**
 * Project one flattened {@link SourceImportStatusItem} onto a transfer-row patch. The phase mapping mirrors
 * the retired per-item poll loops (`pollImport`/`pollIntake`/`pollMcapIngest`) plus {@link sourceImportPatch}
 * so the UI renders the same states — the source-import phase drives while transferring, then the video
 * intake (`intakeState`) or MCAP scanner (`mcapStatus`) drives to a terminal. Pure; exported for tests.
 */
export function deriveStatusPatch(
  status: SourceImportStatusItem,
  ctx: StatusContext,
): Partial<UploadTask> {
  const failure = status.failure ?? undefined;
  const isImport = ctx.channel === 'import';
  switch (status.transferState) {
    case 'pending':
      return { phase: 'queued', importStage: undefined, progress: undefined };
    case 'probing':
      if (ctx.mediaType === 'mcap') {
        return { phase: 'creating', importStage: undefined, progress: undefined };
      }
      return {
        phase: 'prechecking',
        importStage: 'remote-quality',
        progress: batchProgress('probing', status.progress),
      };
    case 'pdq_check':
      if (ctx.mediaType === 'mcap') {
        return { phase: 'creating', importStage: undefined, progress: undefined };
      }
      return {
        phase: 'prechecking',
        importStage: 'remote-duplicate',
        progress: batchProgress('pdq_check', status.progress),
      };
    case 'transferring':
      return {
        phase: 'transferring',
        importStage: 'server-copy',
        progress: batchProgress('transferring', status.progress),
      };
    case 'rejected':
      return {
        phase: batchRejectionPhase(status.rejectKind),
        rejectKind: status.rejectKind ?? undefined,
        reason: failure ?? 'Rejected',
        importStage: undefined,
        progress: undefined,
      };
    case 'failed':
      return {
        phase: 'failed',
        reason: failure ?? 'Import failed',
        importStage: undefined,
        progress: undefined,
      };
  }
  // 'transferred' (import) or no source transfer (direct upload) → post-transfer ingest drives.
  const mediaType = ctx.mediaType ?? (status.mcapStatus != null ? 'mcap' : 'video');
  return mediaType === 'mcap'
    ? mcapStatusPatch(status, failure, isImport)
    : intakeStatusPatch(status, failure, isImport);
}

interface IngestStatus {
  mcapStatus: string | null;
  intakeState: string | null;
  failure: string | null;
  failureCode?: string | null;
  mcapCheckGeneration?: number | null;
  mcapTaskId?: string | null;
  mcapRecheckable?: boolean;
  assetId?: string | null;
  rejectKind?: RejectKind | null;
}

/** Project the local-upload-specific batch contract without inventing source-import state on the
 * wire. The coordinator only starts this lane after upload completion; pending/uploading are treated
 * as a non-terminal reconciliation race and checked again instead of offering a destructive re-upload. */
export function deriveLocalStatusPatch(
  status: LocalSubmissionStatusItem,
  mediaType?: 'video' | 'mcap',
): Partial<UploadTask> {
  if (status.submissionStatus === 'failed' || status.submissionStatus === 'abandoned') {
    return {
      phase: 'failed',
      reason:
        status.submissionStatus === 'abandoned'
          ? 'Upload restarted in a replacement session'
          : (status.failure ?? 'Upload session failed'),
      importStage: undefined,
      progress: undefined,
    };
  }
  if (status.submissionStatus !== 'uploaded') {
    return {
      phase: 'processing',
      reason: undefined,
      importStage: undefined,
      progress: undefined,
    };
  }

  const failure = status.failure ?? undefined;
  const resolvedMediaType = mediaType ?? (status.mcapStatus != null ? 'mcap' : 'video');
  return resolvedMediaType === 'mcap'
    ? mcapStatusPatch(status, failure, false)
    : intakeStatusPatch(status, failure, false);
}
